import { inject } from 'inversify';

import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { CompanyConfigurationStore } from '@/stores/CompanyConfigurationStore';
import type { IFeaturePermissions, IShareTenants } from '@/stores/CompanyConfigurationStore';
import { ICompanyLinking } from '~/api/ICompanyLinking';

@service(CompanyConfigurationManager)
export class CompanyConfigurationManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(CompanyConfigurationStore) protected companyConfigurationStore: CompanyConfigurationStore;

  get all() {
    return this.companyConfigurationStore.all();
  }

  find(id?: string | number) {
    return this.companyConfigurationStore.find(id);
  }

  async load() {
    await this.getFeaturePermissions();
    await this.getCompanyLinkings();
    await this.getSharedTenants();
  }

  get featurePermissionSettings() {
    return this.companyConfigurationStore.featurePermissionSettings;
  }

  updateFeaturePermissionSettings(permissions: IFeaturePermissions | null) {
    this.companyConfigurationStore.setFeaturePermissionSettings(permissions);
  }

  get allSharedTenants() {
    return this.companyConfigurationStore.allSharedTenants;
  }

  updateSharedTenants(tenants: IShareTenants[]) {
    this.companyConfigurationStore.setSharedTenants(tenants);
  }

  async getCompanyLinkings(tenantKey?: string) {
    const { tenantKey: activeTenantKey } = this.userController.activeUser;
    const key = tenantKey || activeTenantKey;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const response = await this.signalr.hubConnection.invoke('GetBridgeItems', key);
    this.companyConfigurationStore.set(response as ICompanyLinking[]);
  }

  async getSharedTenants() {
    const { tenantKey, tenantName } = this.userController.activeUser;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const response = await this.signalr.hubConnection.invoke('GetSharedTenants', tenantKey);
    const ownTenant: IShareTenants = { sharedTenantKey: tenantKey, sharedTenantName: tenantName };
    (response as IShareTenants[]).unshift(ownTenant);
    this.updateSharedTenants(response as IShareTenants[]);
  }

  async getFeaturePermissions() {
    const { tenantKey } = this.userController.activeUser;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const response = await this.signalr.hubConnection.invoke('GetBridge', tenantKey);
    this.updateFeaturePermissionSettings(response as IFeaturePermissions);
  }

  async updateFeaturePermissions(apiAccess: boolean, googleGeocoding: boolean) {
    const { tenantKey } = this.userController.activeUser;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    await this.signalr.hubConnection.invoke('UpdateBridge', tenantKey, apiAccess, googleGeocoding);
    this.updateFeaturePermissionSettings({
      ...this.featurePermissionSettings,
      apiAccess,
      googleGeocoding,
    } as IFeaturePermissions);
  }

  async updateGoogleApiKey(googleApiKey: string) {
    const { tenantKey } = this.userController.activeUser;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    await this.signalr.hubConnection.invoke('UpdateGoogleApiKey', tenantKey, googleApiKey);
    this.updateFeaturePermissionSettings({
      ...this.featurePermissionSettings,
      googleApiKey_custom: googleApiKey,
    } as IFeaturePermissions);
  }

  async saveCompanyLinking(companyLinking: ICompanyLinking) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    await this.signalr.hubConnection.invoke('UpdateBridgeItem', companyLinking.tenantKey, companyLinking.dispatchBridgeItemKey, companyLinking.mappedTenantKey);
    await this.getCompanyLinkings(companyLinking.tenantKey);
    this.companyConfigurationStore.update(companyLinking);
  }
}
